import { Route } from 'vue-router';
import { getPropsFromRoute } from '@/router/router-utils';
import Module, {
  IInitializerConfig,
  TInitEvents
} from './interfaces/module.interface';
import { ObjectToArray } from '@/utils';
import { RootState } from './types';
import { app } from '@/main';
import { modules } from './index';
import { IRouterProps } from '@/router/interfaces/router-props.interface';
import { runAsBackgroundRequest } from '@/utils/backgroundRequest';

const modulesArray = ObjectToArray<TStoreModule>(modules);

type TStoreModule = Module<any, RootState>;
class StoreInitializer {
  async emitEvent(event: TInitEvents, route?: Route) {
    const eventModules = this.getModulesByEvent(event);
    await this.execute(eventModules, route);
  }

  async execute(
    initializers: IInitializerConfig[],
    route: Route = app.$router.currentRoute
  ) {
    const props = getPropsFromRoute(route);

    // Hydration the merchant never asked for: a failure here is logged and
    // retried, never toasted (TWISM-19940).
    //
    // The window is wall-clock, not causal. `isBackgroundRequest()` is read in
    // the request interceptor, so any request *created* while this loop awaits
    // is stamped too, whatever its origin. On the first load nothing else is
    // mounted; on later navigations (`emitEvent('BeforeEachRoute')` awaits this
    // from the router guard) a component still mounted from the previous route
    // can have a request silenced. The loop is a no-op once every initializer
    // has hydrated, so the exposure is a re-run after an earlier failure. The
    // causal version is the initializer threading a flag through the config of
    // the services it calls, instead of this module-level counter.
    await runAsBackgroundRequest(async () => {
      for (const initializer of initializers) {
        if (!initializer.initialized) {
          try {
            await this.initializeStore(initializer, props, initializers);
            initializer.initialized = true;
          } catch (error) {
            initializer.initialized = false;
          }
        }
      }
    });
  }

  private async initializeStore(
    initializer: IInitializerConfig,
    context: IRouterProps,
    initializers: IInitializerConfig[]
  ) {
    const { factory, requires } = initializer;

    if (requires) {
      for (const required of requires) {
        const requiredInit = initializers.find(
          initItem => initItem.provide === required
        );

        if (requiredInit && !requiredInit.initialized) {
          await this.initializeStore(requiredInit, context, initializers);
          // Marked here as well as in `execute`, or a module registered before
          // the one it requires would run its dependency twice per navigation —
          // once through here and once on the dependency's own turn in the loop.
          // That made registration order in `store/index` silently load-bearing
          // (TWISM-19180).
          requiredInit.initialized = true;
        }
      }
    }

    await factory(context);
  }

  private getModulesByEvent(event: TInitEvents) {
    return modulesArray
      .filter(module => module.initializer && module.initializer.on === event)
      .map(module => module.initializer) as IInitializerConfig[];
  }
}

export default new StoreInitializer();
